// Implements: F-CNV-004, F-UIX-009, F-UIX-010
/**
 * The thread of one mockup, as ONE infinite query under `["conversation","messages",id]`.
 *
 * The backend pages forward only (the cursor is the `seq` of the last message seen)
 * and has no "latest N", so the newest message is on the LAST page. Showing the
 * newest at the bottom therefore means reading every page: the hook asks for the
 * next one as soon as the previous has arrived, until `next_cursor` is `null`.
 * The cursor stays opaque: whatever the last page carried is what the next call sends.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { useEffect } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { conversationMessagesKey } from "../../../shared/api/queryKeys";
import type { ConversationMessage, MessagesPageView } from "../../../types/api";
import { fetchMessagesPage } from "../api/conversationApi";

export type UseMessagesResult =
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly error: ApiError; readonly refetch: () => void }
  | {
      readonly status: "success";
      readonly messages: readonly ConversationMessage[];
      /** More pages are being read: the thread is not complete yet. */
      readonly loadingMore: boolean;
      /** A later page could not be read; the pages already read are still in `messages`. */
      readonly moreError: ApiError | null;
      readonly retryMore: () => void;
    };

export function useMessages(mockupId: string): UseMessagesResult {
  const query = useInfiniteQuery<
    MessagesPageView,
    ApiError,
    InfiniteData<MessagesPageView>,
    readonly ["conversation", "messages", string],
    string | undefined
  >({
    queryKey: conversationMessagesKey(mockupId),
    queryFn: ({ pageParam }) => fetchMessagesPage(mockupId, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetching && !isFetchNextPageError) void fetchNextPage();
  }, [hasNextPage, isFetching, isFetchNextPageError, fetchNextPage]);

  if (query.error !== null && query.data === undefined) {
    return {
      status: "error",
      error: query.error,
      refetch: () => {
        void query.refetch();
      },
    };
  }
  if (query.data === undefined) return { status: "pending" };
  return {
    status: "success",
    messages: query.data.pages.flatMap((page) => page.items),
    loadingMore: hasNextPage && !isFetchNextPageError,
    moreError: isFetchNextPageError ? query.error : null,
    retryMore: () => {
      void fetchNextPage();
    },
  };
}
